import { forwardRef } from 'react';
import BaseButton from './base/BaseButton';

/**
 * UniversalActionButton - System-wide action buttons
 * Used for: Add Module, Reset All (at tab level)
 *
 * @param {string} children - Button label
 * @param {function} onClick - Click handler
 * @param {boolean} disabled - Whether button is disabled
 * @param {ReactNode} icon - Optional icon element (typically a plus or reset icon)
 * @param {string} variant - 'default' or 'prominent' (for Reset All style)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
const UniversalActionButton = forwardRef(({
  children,
  onClick,
  disabled = false,
  icon = null,
  variant = 'default',
  className = '',
  style = {},
  ...buttonProps
}, ref) => {
  const isProminent = variant === 'prominent';
  const baseStyle = {
    width: '100%',
    padding: isProminent
      ? 'var(--ui-universal-action-button-prominent-padding-block) var(--ui-universal-action-button-prominent-padding-inline)'
      : 'var(--ui-universal-action-button-padding-block) var(--ui-universal-action-button-padding-inline)',
    borderRadius: 'var(--ui-universal-action-button-radius)',
    fontSize: 'var(--ui-universal-action-button-font-size)',
    fontWeight: isProminent ? '500' : 'normal',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-xs)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    outline: 'none',
    ...style
  };

  // Variant-specific styles
  const variantStyle = isProminent
    ? {
        backgroundColor: disabled ? 'var(--bg-button-disabled)' : 'var(--ui-universal-action-button-prominent-bg)',
        color: 'var(--ui-universal-action-button-prominent-text)',
        border: 'none'
      }
    : {
        backgroundColor: disabled ? 'var(--bg-button-disabled)' : 'var(--ui-universal-action-button-bg)',
        color: disabled ? 'var(--text-disabled)' : 'var(--ui-universal-action-button-text)',
        border: `1px solid ${disabled ? 'var(--border-primary)' : 'var(--ui-universal-action-button-border)'}`
      };

  return (
    <BaseButton
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      className={`universal-action-btn ${className}`}
      style={{
        ...baseStyle,
        ...variantStyle
      }}
      {...buttonProps}
    >
      {icon}
      {children}
    </BaseButton>
  );
});

UniversalActionButton.displayName = 'UniversalActionButton';

export default UniversalActionButton;
